import React, { useState } from 'react';
import { API_BASE } from '../config';
import CombatSettings from './CombatSettings';
import DashboardSettings from './DashboardSettings';
import GeneralSettings from './GeneralSettings';

const TABS = [
  { id: 'general', label: '⚙️ General', icon: '⚙️' },
  { id: 'combat', label: '⚔️ Combat', icon: '⚔️' },
  { id: 'appearance', label: '🎨 Appearance', icon: '🎨' },
  { id: 'campaign', label: '📖 Campaign', icon: '📖' },
  { id: 'guild', label: '🏰 Guild', icon: '🏰' },
];

export default function SettingsModal({ onClose, channels, pinnedChannels, campaignNotes }) {
  const [activeTab, setActiveTab] = useState('general');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Settings</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-layout">
          <div className="settings-tabs">
            {TABS.map(tab => (
              <div
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </div>
            ))}
          </div>
          <div className="settings-content">
            {activeTab === 'general' && <div className="settings-inline"><p style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>General settings coming soon.</p></div>}
            {activeTab === 'combat' && <CombatSettingsInline />}
            {activeTab === 'appearance' && <AppearanceInline channels={channels} pinnedChannels={pinnedChannels} />}
            {activeTab === 'campaign' && <CampaignSettingsInline campaignNotes={campaignNotes} />}
            {activeTab === 'guild' && <GuildSettingsInline />}
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline versions
function GeneralSettingsInline({ channels, pinnedChannels }) {
  return <GeneralSettings onClose={() => {}} channels={channels} pinnedChannels={pinnedChannels} inline />;
}
function CombatSettingsInline() {
  return <CombatSettings onClose={() => {}} inline />;
}
function AppearanceInline({ channels, pinnedChannels }) {
  return (
    <>
      <DashboardSettings onClose={() => {}} inline />
      <div style={{ marginTop: '20px', borderTop: '1px solid var(--panel-border)', paddingTop: '16px' }}>
        <GeneralSettings onClose={() => {}} channels={channels} pinnedChannels={pinnedChannels} inline />
      </div>
    </>
  );
}

function CampaignSettingsInline({ campaignNotes }) {
  const [content, setContent] = useState(campaignNotes || '');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    const session = localStorage.getItem('session');
    if (!session) return;
    try {
      const part = session.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
      fetch(`${API_BASE}/state/${payload.guild_id}`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set_campaign_notes', content, title: 'Campaign Notes' })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  return (
    <div className="settings-inline">
      <h3 style={{ fontSize: '0.8rem', marginBottom: '8px', color: 'var(--text)' }}>📖 Campaign Notes</h3>
      <p style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginBottom: '12px' }}>
        Paste or write your campaign notes in Markdown. They'll appear in the side drawer during sessions.
      </p>
      <textarea
        className="campaign-textarea"
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder={"# Chapter 1: The Arrival\n\nThe party arrives at the old manor...\n\n**Key NPCs:**\n- Aldric (butler)\n- Martha (cook)\n\n## Encounter Trigger\nIf players enter the basement, spawn 3 Shadows."}
        rows={14}
      />
      <div style={{ marginTop: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button className="btn-primary" onClick={handleSave}>Save Notes</button>
        <label className="btn-ghost" style={{ cursor: 'pointer', fontSize: '0.72rem', padding: '6px 12px', border: '1px solid var(--panel-border)', borderRadius: '6px' }}>
          📤 Upload File
          <input type="file" accept=".md,.txt" style={{ display: 'none' }} onChange={e => {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => setContent(ev.target.result);
            reader.readAsText(file);
          }} />
        </label>
        {saved && <span style={{ fontSize: '0.7rem', color: '#2ecc71' }}>✓ Saved</span>}
      </div>
      <p style={{ fontSize: '0.6rem', color: 'var(--text-dim)', marginTop: '8px' }}>
        Supports: # Headings, **bold**, *italic*, - lists, --- dividers. Max 400KB.
      </p>
    </div>
  );
}

const STYLE_OPTIONS = ['RP-Heavy', 'Combat-Focused', 'Casual', 'Deadly', 'Exploration', 'Mystery', 'Horror', 'Political', 'Sandbox'];

function GuildSettingsInline() {
  const [form, setForm] = useState({ guild_name: '', tagline: '', description: '', schedule: '', style_tags: [], looking_for: '', banner_url: '', slug: '', is_public: true });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load current guild profile on mount
  React.useEffect(() => {
    const session = localStorage.getItem('session');
    if (!session) { setLoading(false); return; }
    try {
      const part = session.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
      fetch(`${API_BASE}/state/${payload.guild_id}`, {
        headers: { 'Authorization': 'Bearer ' + session }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.guild_profile) {
            const gp = data.guild_profile;
            setForm({
              guild_name: gp.guild_name || '',
              tagline: gp.tagline || '',
              description: gp.description || '',
              schedule: gp.schedule || '',
              style_tags: gp.style_tags || [],
              looking_for: gp.looking_for || '',
              banner_url: gp.banner_url || '',
              slug: gp.slug || '',
              is_public: gp.is_public !== false,
            });
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } catch { setLoading(false); }
  }, []);

  const handleSave = () => {
    const session = localStorage.getItem('session');
    if (!session) return;
    try {
      const part = session.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
      fetch(`${API_BASE}/state/${payload.guild_id}`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set_guild_profile', ...form })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  const toggleTag = (tag) => {
    setForm(f => ({ ...f, style_tags: f.style_tags.includes(tag) ? f.style_tags.filter(t => t !== tag) : [...f.style_tags, tag].slice(0, 5) }));
  };

  const inputStyle = { width: '100%', padding: '8px', background: 'var(--panel-bg)', border: '1px solid var(--panel-border)', borderRadius: '6px', color: 'var(--text)', fontSize: '0.75rem', boxSizing: 'border-box', marginBottom: '10px' };

  return (
    <div className="settings-inline">
      <h3 style={{ fontSize: '0.8rem', marginBottom: '8px', color: 'var(--text)' }}>🏰 Guild Profile</h3>
      <p style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginBottom: '12px' }}>
        Set up your public guild page. Visible at dndlorewalker.com/guild/your-slug
      </p>

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Guild Name</label>
      <input style={inputStyle} value={form.guild_name} onChange={e => { const name = e.target.value; setForm(f => ({ ...f, guild_name: name, slug: name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 30) })); }} placeholder="The Drowned King's Court" maxLength={50} />

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Tagline</label>
      <input style={inputStyle} value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Where the tide rises, heroes fall." maxLength={100} />

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>URL Slug</label>
      <input style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }} value={form.slug} readOnly placeholder="auto-generated-from-name" />

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Schedule</label>
      <input style={inputStyle} value={form.schedule} onChange={e => setForm(f => ({ ...f, schedule: e.target.value }))} placeholder="Tuesdays & Thursdays 8pm EST" maxLength={200} />

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Looking For</label>
      <input style={inputStyle} value={form.looking_for} onChange={e => setForm(f => ({ ...f, looking_for: e.target.value }))} placeholder="1 Healer, experience: any" maxLength={200} />



      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Description</label>
      <textarea className="campaign-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Tell visitors about your campaign..." rows={4} maxLength={1000} />

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginTop: '8px', display: 'block' }}>Style Tags (pick up to 5)</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', marginTop: '6px' }}>
        {STYLE_OPTIONS.map(tag => (
          <span key={tag} onClick={() => toggleTag(tag)} style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '0.65rem', cursor: 'pointer', background: form.style_tags.includes(tag) ? 'var(--accent)' : 'var(--panel-bg)', border: '1px solid var(--panel-border)', color: form.style_tags.includes(tag) ? '#fff' : 'var(--text-dim)' }}>{tag}</span>
        ))}
      </div>

      <label style={{ fontSize: '0.65rem', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <input type="checkbox" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))} /> Make guild page public
      </label>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button className="btn-primary" onClick={handleSave}>Save Guild Profile</button>
        {saved && <span style={{ fontSize: '0.7rem', color: '#2ecc71' }}>✓ Saved</span>}
      </div>
    </div>
  );
}
